<template>
  <WizzardContainer
    v-if="asset && transaction"
    :model-value="currentStep"
    v-bind="{ isOpen, zIndex }"
    :title
    :steps="mappedSteps"
    @close="handleClose"
    @update:model-value="handleStepChange"
  >
    <slot :name="`step${currentStep + 1}`" :account :asset :transaction :tags>
      <component
        :is="currentComponent"
        ref="currentComponentRef"
        v-bind="{
          hasExistingAccounts,
          account,
          asset,
          transaction,
          tags,
        }"
        v-model:prices="prices"
        v-model:use-existing-account="useExistingAccount"
        @update:tags="tags = $event"
      />
    </slot>
    <template #buttons>
      <UIButton
        v-bind="{ disabled }"
        :label="buttonLabel"
        tabindex="11"
        @click="handleButtonClick"
      />
    </template>
    <template v-if="loading" #loading>
      Please wait. <br />We are creating your {{ name }}...
    </template>
  </WizzardContainer>
</template>

<script setup lang="ts">
import { computed, onMounted, provide, ref, useTemplateRef, watch } from 'vue'
import { useIntervalFn } from '@vueuse/core'

import {
  Asset,
  AssetClass,
  AssetPriceForm,
  ModalClass,
  StepsNavigationStatus,
  StepsNavigation,
  TransactionClass,
} from '@types'
import { InputWizardPreset } from '../utils/types'

import { ASSET_CLASS, INVESTMENT_STRATEGY } from '../utils/const'
import { ASSET_FIELD, NOTIFICATION_DELAY } from '@/const'

import { getCurrencySymbol } from '@/helpers/common'
import { getAssetListValues } from '@/entities/transactions/components/utils/helpers'
import { getCurrentDate } from '@/helpers/dates'
import { initData, processTags, resetData } from '../utils/helpers'

import { useNotifications } from '@/plugins/notification'

import useAnalyticsStore from '@/store/analytics'
import { useAssetsBunchStore } from '@/store/assets/bunch'
import { useAssetPricesStore } from '@/store/assets/prices'
import { useRepositoriesStore } from '@/store/repositories/index'
import { useTagsBunchStore } from '@/store/tags/bunch'
import { useTransactionsSettingsStore } from '@/store/transactions/settings'

import { UIButton } from '@ui/buttons'
import StepAccount from '../modules/StepAccount.vue'
import StepData from '../modules/StepData.vue'
import StepValues from '../modules/StepValues.vue'
import StepTags from '../modules/StepTags.vue'
import StepSummary from '../modules/StepSummary.vue'
import WizzardContainer from '../components/WizzardContainer.vue'

type Props = {
  title: string
  name: string
  modal: ModalClass<any>

  steps: StepsNavigation[]

  transactionType: string

  accountType?: string
  assetType?: string

  assetClassesList: string[]
  investmentStrategiesList: string[]

  preset?: InputWizardPreset

  disallowExistingAccount?: boolean
  disableValuesStep?: boolean

  handleBeforeSave?: (
    accountId?: string | null,
    assetId?: string | null,
    currencyId?: string | null,
  ) => Promise<void>

  handleStepStatus?: (index: number) => boolean
}

type Emits = {
  init: []
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

defineExpose({
  setTransaction: (
    callback: (transaction: TransactionClass, date: string, accountId?: string | null) => void,
  ) => {
    callback(transaction.value!, defaultDate.value, accountId.value)
    setTimeout(() => {
      transaction.value?.validateData()
    }, 0)
  },
})

const { error, success } = useNotifications()

const analyticsStore = useAnalyticsStore()
const assetsBunchStore = useAssetsBunchStore()
const assetsPricesStore = useAssetPricesStore()
const repositoriesStore = useRepositoriesStore()
const tagsBunchStore = useTagsBunchStore()
const transactionsSettingsStore = useTransactionsSettingsStore()

const { pause, resume } = useIntervalFn(async () => {
  if (
    (await currentComponentRef.value?.nextStepDisallowed?.()) ||
    currentStep.value === summaryStep.value ||
    !mappedSteps.value[currentStep.value].status
  ) {
    pause()
  } else {
    currentStep.value++
  }
}, 10)
pause()

provide('accountLabel', props.steps[0].name)
provide('assetLabel', props.steps[1].name)
provide('tagAssetClassList', props.assetClassesList)
provide('tagInvestmentStrategyList', props.investmentStrategiesList)

const currentComponentRef = useTemplateRef<any>('currentComponentRef')

const currentStep = ref(0)
const loading = ref(false)

const useExistingAccount = ref(!props.disallowExistingAccount)

const account = ref<AssetClass>()
const asset = ref<AssetClass>()
const transaction = ref<TransactionClass>()
const prices = ref<AssetPriceForm[]>([])
const tags = ref<Record<string, string | undefined>>({
  [ASSET_CLASS]: undefined,
  [INVESTMENT_STRATEGY]: undefined,
})

const currentComponent = computed(() => {
  switch (currentStep.value) {
    case 0:
      return StepAccount
    case 1:
      return StepData
    case valuesStep.value:
      return StepValues
    case tagsStep.value:
      return StepTags
    case summaryStep.value:
      return StepSummary
    default:
      return null
  }
})

const zIndex = computed(() => props.modal.zIndex)
const isOpen = computed(() => props.modal.isOpened)

const defaultDate = computed(() => analyticsStore.getDate || getCurrentDate({}))
provide('defaultDate', defaultDate)

const buttonLabel = computed(() => {
  switch (currentStep.value) {
    case summaryStep.value:
      return `Create ${props.name}`
    default:
      return 'Next'
  }
})

const accountId = computed(() => transaction.value?.entry1().value?.account_id)

const isValidAccount = computed(
  () => (useExistingAccount.value && accountId.value) || account.value?.isValid,
)

const isValidAsset = computed(() => asset.value?.isValid)

const isValidTransaction = computed(() => {
  if (!transaction.value) return false
  const keys = Object.keys(transaction.value.errors)
  const entry1Title = transaction.value.settings?.entries[0]?.title
  const entry2Title = transaction.value.settings?.entries[1]?.title
  return (
    transaction.value.isValid ||
    keys.every(key =>
      [
        `"${entry1Title}" account`,
        `"${entry1Title}" asset`,
        `"${entry2Title}" asset`,
      ].includes(key),
    )
  )
})

const isTagsFilled = computed(() => {
  return Object.values(tags.value).every(tag => tag)
})

const valuesStep = computed(() =>
  props.disableValuesStep ? -1 : props.steps.length - 3,
)
const tagsStep = computed(() => props.steps.length - 2)
const summaryStep = computed(() => props.steps.length - 1)

const mappedSteps = computed(() =>
  props.steps.map((item, index) => {
    let status
    switch (index) {
      case 0:
        status = isValidAccount.value
          ? StepsNavigationStatus.COMPLETED
          : undefined
        break
      case 1:
        status =
          isValidTransaction.value && isValidAsset.value
            ? StepsNavigationStatus.COMPLETED
            : undefined
        break
      case valuesStep.value:
        status = prices.value.length
          ? StepsNavigationStatus.COMPLETED
          : StepsNavigationStatus.SKIPPED
        break
      case tagsStep.value:
        status = isTagsFilled.value
          ? StepsNavigationStatus.COMPLETED
          : undefined
        break
      case summaryStep.value:
        break
      default:
        status = props.handleStepStatus?.(index)
          ? StepsNavigationStatus.COMPLETED
          : StepsNavigationStatus.SKIPPED
        break
    }
    return {
      ...item,
      status,
    }
  }),
)

const disabled = computed(() => {
  const status = mappedSteps.value[currentStep.value].status
  return (
    (!status ||
      ![
        StepsNavigationStatus.COMPLETED,
        StepsNavigationStatus.SKIPPED,
      ].includes(status)) &&
    currentStep.value !== mappedSteps.value.length - 1
  )
})

const repositoryId = computed(() => repositoriesStore.getCurrentId)
const currency = computed(() => repositoriesStore.getCurrentCurrency)

const assetCurrency = computed(
  () =>
    asset.value?.field<string>(ASSET_FIELD.CURRENCY).value || currency.value,
)

const currencySymbol = computed(() => getCurrencySymbol(assetCurrency.value))
provide('currency', currencySymbol)

const hasExistingAccounts = computed(() => {
  if (!transaction.value?.settings || props.disallowExistingAccount)
    return false
  const data = Array.from(assetsBunchStore.getList.values()).map(instance =>
    instance.get(),
  )

  return !!getAssetListValues(
    transaction.value.settings.entries[0].account,
    data,
  )?.length
})

const handleClose = () => {
  props.modal.close()
}

const handleCreate = async () => {
  if (!asset.value || !transaction.value) return
  loading.value = true
  try {
    const createAccount = !useExistingAccount.value && !!account.value
    if (createAccount) {
      await account.value?.store()
      account.value &&
        transaction.value.pasteAccountId1(account.value.id, false)
    }

    const tagsIds = await processTags(tags.value, tagsBunchStore)
    if (tagsIds.length) {
      asset.value.field<string[]>(ASSET_FIELD.TAGS).value = tagsIds
    }
    await asset.value.store()

    const currencyAsset = asset.value?.field<Asset | undefined>(
      ASSET_FIELD.CURRENCY_ASSET,
    ).value
    let currencyId = currencyAsset?.id
    if (!currencyId) {
      currencyId = assetsBunchStore.getElementByTicker(assetCurrency.value)?.id
    }

    transaction.value.pasteAssetId1(asset.value.id, false)
    currencyId && transaction.value.pasteAssetId2(currencyId, false)
    props.handleBeforeSave?.(accountId.value, asset.value.id, currencyId)
    await transaction.value.store()

    // create prices if exists
    prices.value?.length &&
      (await Promise.all(
        prices.value.map(
          price =>
            asset.value &&
            assetsPricesStore.store({
              ...price,
              asset_id: asset.value.id,
            }),
        ),
      ))

    await success(
      {
        message: `Your ${props.name} has been successfully created`,
      },
      NOTIFICATION_DELAY,
    )

    // open asset summary
    asset.value?.openDrawer()
    handleClose()
  } catch (e) {
    await error({ message: e instanceof Error ? e.message : 'Something went wrong' })
  } finally {
    loading.value = false
  }
}

const handleStepChange = async (value: number) => {
  if (await currentComponentRef.value?.nextStepDisallowed?.()) return
  currentStep.value = value
}

const handleButtonClick = async () => {
  if (await currentComponentRef.value?.nextStepDisallowed?.()) return
  if (currentStep.value === summaryStep.value) {
    handleCreate()
    return
  }
  currentStep.value++
}

watch(
  hasExistingAccounts,
  value => {
    if (value === undefined || props.preset) return
    useExistingAccount.value = value
  },
  { immediate: true },
)

watch(
  () => account.value?.field(ASSET_FIELD.NAME).value,
  value => {
    account.value && (account.value.field(ASSET_FIELD.TICKER).value = value)
  },
)

watch(
  () => asset.value?.field(ASSET_FIELD.NAME).value,
  value => {
    asset.value && (asset.value.field(ASSET_FIELD.TICKER).value = value)
  },
)

watch(
  isOpen,
  value => {
    if (!repositoryId.value) return
    if (!value) {
      currentStep.value = 0
      resetData(account, asset, transaction, prices, tags)
      emit('init')
      return
    }
    initData(
      props.transactionType,
      defaultDate.value,
      asset,
      account,
      transaction,
      prices,
      tags,
      repositoryId.value,
      currency.value,
      props.assetClassesList,
      props.investmentStrategiesList,
      props.accountType,
      props.assetType,
      props.preset,
    )
    if (props.preset) {
      if (!props.disallowExistingAccount) {
        useExistingAccount.value = !props.preset?.account?.isNew
      }
      resume?.()
    } else {
      useExistingAccount.value = !props.disallowExistingAccount
    }
  },
  { immediate: true },
)

onMounted(() => {
  if (!transactionsSettingsStore.initFlag) {
    transactionsSettingsStore.fetch()
  }
})
</script>
